import { Box, Divider, Popover, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { formatDistanceToNow } from 'date-fns';
import { AllCommentsGenericQuery } from '@/__generated__/graphql';
import { useMemo } from 'react';
import { CommentOptionsMenu } from './CommentOptionsMenu';

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  comment: NonNullable<AllCommentsGenericQuery['allComments']>[number] | null;
  onEdit?: () => void;
  onDelete?: () => void;
};

export const KpiFloatingCommentViewer = ({
  anchorEl,
  onClose,
  comment,
  onEdit,
  onDelete,
}: Props) => {
  const open = Boolean(anchorEl);

  const timeAgo = useMemo(() => {
    if (!comment?.createdAt) return 'N/A';
    return formatDistanceToNow(new Date(Number(comment.createdAt)), { addSuffix: true });
  }, [comment]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            ml: -8,
            border: `1px solid ${abyssColors.primary[200]}`,
            borderRadius: '8px',
            minWidth: '20vw',
            maxWidth: '30vw',
          },
        },
      }}
    >
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
          }}
        >
          <Typography color="primary" variant="subtitle2">
            Note
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: abyssColors.primary[200] }} />

        {/* Author and timestamp */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1 }}>
          <Typography variant="body2" fontWeight={600} color="primary">
            {comment?.createdByDetails?.name || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {timeAgo}
          </Typography>

          <CommentOptionsMenu
            comment={comment}
            onEdit={onEdit}
            onDelete={() => {
              onClose();
              onDelete?.();
            }}
          />
        </Box>

        {/* Comment text */}
        <Box sx={{ px: 1, mb: 1 }}>
          <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
            {comment?.text || 'N/A'}
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};
